import { StyledForm } from './style';
import { CategorySelector } from '../Select/Selectors/CategorySelectors';
import { LocationSelector } from '../Select/Selectors/LocationSelector';
import { FarmsSelector } from '../Select/Selectors/FarmsSelector';
import { QualityMarkCheckboxes } from '../Checkbox/Checkboxes/QualityMarkCheckboxes';
import { AdditionalOptionsCheckboxes } from '../Checkbox/Checkboxes/AdditionalOptionsCheckboxes';
import { DeliveriesCheckboxes } from '../Checkbox/Checkboxes/DeliveriesCheckboxes';
import { EmarketCheckbox } from '../Checkbox/Checkboxes/EmarketCheckbox';
import { CooperativeCheckbox } from '../Checkbox/Checkboxes/CooperativeCheckbox';
import { DynamicPurchaseCheckbox } from '../Checkbox/Checkboxes/DynamicPurchaseCheckbox';
import { useEffect } from 'react';
import { Filter } from 'types/Filter';
import { FilterOptions } from 'types/FilterOptions';
import { useIntl } from 'react-intl';
import { LocationRadiusSelector } from 'ui/Select/Selectors/LocationRadiusSelector';
import { getCategoryFieldIds } from 'utils/filterCategoryFields';
import { DistanceRadiusSelector } from 'ui/Select/Selectors/DistanceRadiusSelector';
import { AddressSelector } from 'ui/Select/Selectors/AddressSelector';
import { Checkbox } from '../Checkbox';
import useMarketType from 'utils/useMarketType';
import { Button } from 'ui';
import {
  applyDefaultDistanceRadius,
  clearAddressWhenLocationChosen,
} from 'utils/filterHelper';
import type { FieldData } from 'rc-field-form/lib/interface';

interface Props {
  setFilter: (newFilter: Filter) => void;
  filterOptions: FilterOptions;
  filter: Filter;
  mapView?: boolean;
  onCloseFilter?: () => void;
  onClearFilters?: () => void;
  onExport?: () => void;
  exportLoading?: boolean;
}
export const Form = ({
  setFilter,
  filterOptions,
  filter,
  mapView,
  onCloseFilter,
  onClearFilters,
  onExport,
  exportLoading,
}: Props) => {
  const [form] = StyledForm.useForm();
  const intl = useIntl();
  const categoryFieldIds = getCategoryFieldIds(filter);
  const includeNonEmarketCategories = !!filterOptions.emarket;
  const { marketType } = useMarketType();
  const isWholesale = marketType === 'WHOLESALE';
  const hasLocation = Array.isArray(filter?.location)
    ? filter.location.length > 0
    : !!filter?.location;
  const hasAddress = !!filter?.address;

  useEffect(() => {
    if (!!filter) {
      form.setFieldsValue(filter);
    }
  }, [form, filter]);

  const handleFieldsChange = (changedFields?: FieldData[]) => {
    clearAddressWhenLocationChosen(form, changedFields);
    const values = applyDefaultDistanceRadius(form, marketType);
    setFilter(values);
  };

  const handleClearFilters = (): void => {
    form.resetFields();
    onClearFilters?.();
  };

  return (
    <div className={'form-container'}>
      <StyledForm
        layout={'vertical'}
        onFieldsChange={handleFieldsChange}
        form={form}
      >
        <div className={'categories-form'}>
          <CategorySelector
            includeNonEmarketCategories={includeNonEmarketCategories}
            mainCategoryValue={categoryFieldIds.mainCategoryValue}
            subCategoryValue={categoryFieldIds.subCategoryValue}
            subSubCategoryValue={categoryFieldIds.subSubCategoryValue}
          />
          {filterOptions.locations_radius ? (
            <>
              <LocationRadiusSelector
                locations={filterOptions.locations_radius}
                form={form}
              />
              {isWholesale && (
                <AddressSelector form={form} onChange={handleFieldsChange} />
              )}
              {isWholesale && hasAddress && !filter?.location_radius && (
                <DistanceRadiusSelector />
              )}
            </>
          ) : (
            <>
              <LocationSelector
                locations={filterOptions?.locations}
                form={form}
              />
              {isWholesale && (
                <AddressSelector form={form} onChange={handleFieldsChange} />
              )}
              {isWholesale && (hasLocation || hasAddress) && (
                <DistanceRadiusSelector />
              )}
            </>
          )}
          <FarmsSelector farms={filterOptions?.farms} form={form} />
          {filterOptions?.emarket && marketType !== 'WHOLESALE' && (
            <EmarketCheckbox emarket={filterOptions.emarket} />
          )}
          <CooperativeCheckbox cooperative={filterOptions?.cooperative} />
          <DynamicPurchaseCheckbox />
          <QualityMarkCheckboxes qmarks={filterOptions?.qmark} />
          <AdditionalOptionsCheckboxes services={filterOptions?.services} />
          {marketType !== 'WHOLESALE' && (
            <DeliveriesCheckboxes delivery={filterOptions?.delivery} />
          )}
          {onClearFilters && (
            <div className={'clear-filters-button-wrapper'}>
              <Button
                type={'default'}
                htmlType={'button'}
                block
                onClick={handleClearFilters}
                label={intl.formatMessage({ id: 'general.delete_filters' })}
              />
            </div>
          )}
          {marketType === 'WHOLESALE' && onExport && (
            <div className={'export-product-list-wrapper'}>
              <Button
                type={'default'}
                block
                onClick={onExport}
                disabled={exportLoading}
                loading={exportLoading}
                label={
                  exportLoading
                    ? intl.formatMessage({ id: 'general.loading' })
                    : intl.formatMessage({ id: 'products.save_list' })
                }
              />
            </div>
          )}
          {mapView && (
            <div className={'close-btn-wrapper'}>
              <button
                className="close-form-btn"
                type="button"
                onClick={onCloseFilter}
              >
                {intl.formatMessage({ id: 'general.close' })}
              </button>
            </div>
          )}
        </div>
      </StyledForm>
    </div>
  );
};
